/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Vers.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { usePress } from '../hooks/UNSAFE_usePress';
import { useHover } from '../hooks/UNSAFE_useHover';
import { useActive } from '../hooks/UNSAFE_useActive';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { useColorScheme } from '../hooks/UNSAFE_useColorScheme';
import { classNames } from '../utils/UNSAFE_classNames';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { multiVariantStyles } from './themes/BaseButtonStyles.css';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';

export type ActionDetail = {
  reason: 'pointer' | 'keyboard';
};

type BaseButtonElementDetails =
  | { type: 'button'; isFocusable?: never; buttonType?: 'button' | 'submit' }
  | { type: 'a'; href: string; target?: string; rel?: string; isFocusable?: never }
  | { type: 'span'; isFocusable?: boolean };

type IntrinsicProps = Pick<
  HTMLAttributesSignalExcluded<HTMLElement>,
  | 'autofocus'
  | 'title'
  | 'onBlur'
  | 'onFocus'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'onTouchEnd'
  | 'onTouchStart'
  | 'onKeyDown'
>;
export type StylingOptions =
  | 'default'
  | 'min'
  | 'embedded'
  | 'container'
  | 'unstyled'
  | 'fill'
  | 'noBorderRadiusEnd'
  | 'noBorderRadiusStart'
  | 'active';

type AriaProps = {
  'aria-label'?: string;
  'aria-hidden'?: boolean;
  'aria-expanded'?: boolean;
  'aria-roledescription'?: string;
  'aria-describedby'?: string;
  'aria-haspopup'?: string;
};

export type Props = IntrinsicProps &
  AriaProps &
  DimensionProps &
  TestIdProps & {
    /**
     *The default slot is the button's text label. The oj-c-base-button element accepts plain text or DOM nodes as children for the default slot."
     */
    children?: ComponentChildren;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * Specifies if repeated events are dispatched for key or pointer held down
     */
    isRepeat?: boolean;

    /**
     *
     * Indicates in what states the button has chrome (background and border).
     *     "ghost": "Ghost buttons are the least prominent variation.  Ghost buttons are
     * useful for performing low-priority tasks, such as manipulating the UI.",
     *     "borderless"': "Borderless buttons are a more prominent variation. Borderless
     * buttons are useful for supplemental actions that require minimal emphasis.",
     *     "outlined"': "Outlined buttons are salient, but lighter weight than
     * solid buttons. Outlined buttons are useful for secondary actions.",
     *     "solid"': "Solid buttons stand out, and direct the user's attention to the
     *  most important actions in the UI.",
     *     "callToAction"': "A Call To Action (CTA) button guides the user to take or
     * complete the action that is the main goal of the page or page section. There
     * should only be one CTA button on a page at any given time.",
     *     "danger"': "A Danger button alerts the user to a dangerous situation."
     */
    variant?: 'ghost' | 'borderless' | 'outlined' | 'solid' | 'callToAction' | 'danger';

    /**
     * The size indicates how large the button is rendered: 'xs', "sm", "md" (default), "lg".
     */
    size?: 'xs' | 'sm' | 'md' | 'lg';

    /**
     * Edge can be used to specify that the button is intended to go at the bottom of
     * a region, stretching from left to right: "bottom" or "none" (default).
     */
    edge?: 'none' | 'bottom';

    /**
     * Styling can be used for customizing buttons by removing label padding ("min") or
     * removing all styling ("unstyled").
     */
    styling?: StylingOptions[];

    /**
     * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
     */
    onAction?: (details: ActionDetail) => void;

    /**
     * Indicate element type and associated details
     */
    elementDetails?: BaseButtonElementDetails;
  };

const interpolations = [...Object.values(dimensionInterpolations)];
const styleInterpolations = mergeInterpolations<Props>(interpolations);

const clientHints = getClientHints();
// :active only works on IOS devices if a touch handler exists
const iosProps = clientHints.platform === 'ios' ? { ontouchstart: function () {} } : {};
const isHybrid = getClientHints().hoverSupport === 'events';

const roleProps = (
  elType: string,
  hidden: boolean,
  isFocusable: boolean
): { role?: HTMLAttributesSignalExcluded['role'] } => {
  switch (elType) {
    case 'link':
      return { role: 'link' };
    case 'button':
      return {};
    default:
      return hidden ? { role: 'none' } : isFocusable ? { role: 'button' } : { role: 'img' };
  }
};

export const BaseButton = forwardRef(
  (
    {
      variant,
      isDisabled = false,
      isRepeat = false,
      size,
      elementDetails = { type: 'button', buttonType: 'button' },
      styling = ['default'],
      edge = 'none',
      'aria-label': accessibleLabel,
      'aria-hidden': ariaHidden,
      'aria-expanded': ariaExpanded,
      'aria-roledescription': ariaRoledescription,
      'aria-haspopup': ariaHasPopup,
      'aria-describedby': ariaDescribedBy,
      testId,
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onTouchEnd,
      onTouchStart,
      onKeyDown,
      ...props
    }: Props,
    ref?:
      | ForwardedRef<HTMLButtonElement>
      | ForwardedRef<HTMLAnchorElement>
      | ForwardedRef<HTMLSpanElement>
  ) => {
    const pressHandler = (e: Event) => {
      if (props.onAction) {
        props.onAction({
          reason: e.type == 'keyup' ? 'keyboard' : 'pointer'
        });
      }
    };

    const { pressProps } = usePress(pressHandler, {
      isDisabled,
      isRepeat
    });
    const isStyled = (name: StylingOptions) => {
      return styling.indexOf(name) > -1;
    };
    const { hoverProps, isHover } = useHover({ isDisabled: !isHybrid });
    const { activeProps, isActive } = useActive();
    const { class: cls, ...styles } = styleInterpolations(props);
    const {
      type: elementType = 'button',
      isFocusable: isElementFocusable = true,
      buttonType = 'button',
      ...elementProps
    } = { ...elementDetails };
    const typeProps =
      elementType === 'button' && buttonType === 'button' ? { type: buttonType } : {};

    // 'role' should be 'none' when aria-hidden is true or 'img' when false and not focusable
    const role = roleProps(elementType, ariaHidden ?? false, isElementFocusable);
    const ElementType = elementDetails.type;
    const isSpan = ElementType == 'span';
    const isBaseButton = ElementType == 'button';
    const { tabbableModeProps } = useTabbableMode(isDisabled);
    const colorScheme = useColorScheme();

    const variantClasses = multiVariantStyles({
      size,
      variant,
      unstyled: isStyled('unstyled') ? 'isUnstyled' : 'notUnstyled',
      embedded: isStyled('embedded') ? 'isEmbedded' : 'notEmbedded',
      container: isStyled('container') ? 'isContainer' : 'notContainer',
      noBorderRadiusStart: isStyled('noBorderRadiusStart')
        ? 'isNoBorderRadiusStart'
        : 'notNoBorderRadiusStart',
      noBorderRadiusEnd: isStyled('noBorderRadiusEnd')
        ? 'isNoBorderRadiusEnd'
        : 'notNoBorderRadiusEnd',
      activeStyle: isStyled('active') ? 'isActiveStyle' : 'notActiveStyle',
      fill: isStyled('fill') ? 'isFill' : 'notFill',
      min: isStyled('min') ? 'isMin' : 'notMin',
      disabled: isDisabled ? 'isDisabled' : 'notDisabled',
      edge,
      hybrid: isHybrid ? 'isHybrid' : 'notHybrid',
      active: isActive ? 'isActive' : 'notActive',
      hover: isHover ? 'isHover' : 'notHover',
      span: isSpan ? 'isSpan' : 'notSpan',
      colorScheme
    });

    const globalEventProps = {
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onTouchEnd,
      onTouchStart,
      onKeyDown
    };

    const mergedProps = mergeProps(
      elementProps,
      pressProps,
      iosProps,
      hoverProps,
      isStyled('container') ? {} : activeProps,
      isBaseButton ? {} : { 'aria-disabled': isDisabled },
      globalEventProps
    );

    const wrapperClasses = classNames([cls, variantClasses]);
    const testIdProps = useTestId(testId);

    const getTabIndex = () => {
      if (isSpan && (isElementFocusable === false || isDisabled)) {
        // no tabIndex value should be defined for a span element when disabled or non-focusable
        return { tabIndex: undefined };
      }
      return tabbableModeProps;
    };

    return (
      <ElementType
        // The ref casting was done to satisfy TS compiler, when the forwardRef had to be updated to ForwardedRef.
        ref={
          ref as ForwardedRef<HTMLButtonElement> &
            ForwardedRef<HTMLAnchorElement> &
            ForwardedRef<HTMLSpanElement>
        }
        disabled={isDisabled}
        class={wrapperClasses}
        style={styles}
        autofocus={props['autofocus']}
        title={props.title}
        {...getTabIndex()}
        aria-label={accessibleLabel}
        aria-haspopup={ariaHasPopup ? 'menu' : undefined}
        aria-describedby={ariaDescribedBy}
        aria-hidden={ariaHidden}
        aria-roledescription={ariaRoledescription}
        aria-expanded={ariaExpanded}
        {...typeProps}
        {...testIdProps}
        {...mergedProps}
        {...role}>
        {props.children}
      </ElementType>
    );
  }
);

BaseButton.displayName = 'BaseButton';
